import { Text } from "./Text";
import { Numeric } from "./Numeric";
import { Checkbox } from "./Checkbox";
import { Radio } from "./Radio";
import { Select } from "./Select";

import { Stack, styled, Typography, useTheme } from "@mui/material";
import { PropertyValue } from "src/context/Property/Builder/PropertyBuilder";
import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";
import { BlockItemValue, ValueDisplay } from "@foodpilot/foods";
import { deserializeReadOnly } from "src/context/Property/PropertyValueSerializer";
import { CalculationType, useUpdateProperty } from "src/api/client-api/property/useUpdateProperty";
import { useActionsContext } from "src/context/Actions/useActionsContext";

export type OnUpdate = {
    serializedString: string;
    isGenericValue?: boolean;
    dataListOptionIds?: number[];
    calculationType?: CalculationType;
};

type _ValueSwitchProps = {
    property: PropertyValue;
    comparisonProperty: PropertyValue | undefined;
    shouldShowSubscore?: boolean;
};
const _ValueSwitch = (props: _ValueSwitchProps) => {
    const { property, shouldShowSubscore, comparisonProperty } = props;
    const { savingStatus } = useActionsContext();
    const updateProperty = useUpdateProperty();

    const updatePropertyValue = (options: OnUpdate) => {
        const { serializedString, isGenericValue, dataListOptionIds, calculationType = "questionnaire" } = options;
        savingStatus.setSaving(true);
        updateProperty.mutate(
            {
                postId: property.postId,
                propertyId: property.property.id,
                value: serializedString,
                position: property.position,
                isGenericValue,
                dataListOptionIds,
                calculationType,
            },
            {
                onSettled: () => {
                    savingStatus.setSaving(false);
                },
            },
        );
    };

    const commonProps = {
        property,
        updatePropertyValue,
        isLocked: savingStatus.isSaving,
        shouldShowSubscore,
        comparisonProperty,
    };

    if (property.fieldType === "text") {
        const isPropertyString = property.property.unit?.title?.toLowerCase() === "string";
        if (isPropertyString) {
            return <Text {...commonProps} />;
        }

        return <Numeric {...commonProps} />;
    }

    if (property.fieldType === "checkbox") {
        return <Checkbox {...commonProps} />;
    }

    if (property.fieldType === "radio") {
        return <Radio {...commonProps} />;
    }

    if (property.fieldType === "select") {
        return <Select {...commonProps} />;
    }
};

type ValueSwitchProps = {
    title: string | undefined;
    comparisonProperty: PropertyValue | undefined;
    property: PropertyValue | undefined;
    variant?: "readonly" | "default";
    shouldShowSubscore?: boolean;
};
export const ValueSwitch = (props: ValueSwitchProps) => {
    const { title, variant = "default", property, shouldShowSubscore = false, comparisonProperty } = props;

    if (property === undefined) {
        return <Typography variant="body-bold">{"No value"}</Typography>;
    }

    const { labels } = deserializeReadOnly(property);
    return (
        <Stack>
            {title !== undefined && <Typography variant={"body-bold"}>{title}</Typography>}
            {variant === "default" ?
                <_ValueSwitch
                    comparisonProperty={comparisonProperty}
                    property={property}
                    shouldShowSubscore={shouldShowSubscore}
                />
            :   <ReadonlyProperty title={""} values={labels} unit={property.unit} />}
        </Stack>
    );
};

type ReadonlyPropertyProps = {
    title?: string;
    values: BlockItemValue;
    unit?: string;
};
const ReadonlyProperty = (props: ReadonlyPropertyProps) => {
    const theme = useTheme();
    const { title, values, unit } = props;

    return (
        <Fragment>
            {title !== undefined && <Typography variant="body-bold">{title}</Typography>}
            {Array.isArray(values) ?
                <ValueDisplay value={values} />
            :   <Stack flexDirection={"row"} gap={theme.spacing(1)}>
                    <ValueDisplay value={values} />
                    {unit !== undefined && <Typography variant="body-medium">{unit}</Typography>}
                </Stack>
            }
        </Fragment>
    );
};

type ValueSwitchContainerProps = {
    startProperty: PropertyValue;
    endProperty: PropertyValue;
};
export const ValueSwitchContainer = (props: ValueSwitchContainerProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { startProperty, endProperty } = props;

    const startTitle = t("actionPlan.value_in_year", { year: startProperty.campaign?.label });
    const targetTitle = `${t("Goal")} ${endProperty.campaign?.label}`;
    return (
        <Stack gap={theme.spacing(1)}>
            <PropertyHeader startTitle={startTitle} targetTitle={targetTitle} />
            <Stack flexDirection={"row"} alignItems={"space-between"}>
                <CenteredStack borderRight={`1px solid ${theme.custom.grey[1400]}`}>
                    <ValueSwitch
                        comparisonProperty={undefined}
                        property={startProperty}
                        title={undefined}
                        variant="readonly"
                    />
                </CenteredStack>
                <CenteredStack>
                    <ValueSwitch comparisonProperty={startProperty} property={endProperty} title={undefined} />
                </CenteredStack>
            </Stack>
        </Stack>
    );
};

const CenteredStack = styled(Stack)(({ theme }) => ({
    width: "50%",
    justifyContent: "center",
    alignItems: "start",
    paddingLeft: theme.spacing(2),
}));

type PropertyHeaderProps = {
    startTitle?: string;
    targetTitle?: string;
};
export const PropertyHeader = (props: PropertyHeaderProps) => {
    const { startTitle, targetTitle } = props;

    return (
        <Stack flexDirection={"row"}>
            <CenteredStack>
                <Typography variant="body-bold">{startTitle}</Typography>
            </CenteredStack>
            <CenteredStack>
                <Typography variant="body-bold">{targetTitle}</Typography>
            </CenteredStack>
        </Stack>
    );
};
